/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import TwMap from '../../components/TwMap/TwMap';
import TwMarker, {
  LiveLocationMarker,
  LocationMarker,
  LocationMarkerType
} from '../../components/TwMarker/TwMarker';

import { ReactComponent as TruckbaseLogo } from '../../assets/truckbase-logo.svg';
import { ReactComponent as MobileLogo } from '../../assets/tb-logo.svg';
import useDirections from '../../hooks/useGoogleDirections';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import useGoogleMaps from '../../providers/GoogleMaps/GoogleMaps.provider';
import { PublicLoadTenant } from '../../providers/Guest/Guest.model';

import StringUtils from '../../utils/stringUtils';
import { addLogRocketIdentityForSharedLink } from '../../utils/logRocketUtils';

import './PublicLoadPage.scss';
import { getMarkerIcon, getMapMarkers, getLiveLocationMarker } from '../../utils/mapUtils';
import LoadUtils from '../../utils/loadUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import { LOAD_TYPE, LoadClient, LoadLocation } from '../../redux/api/load/load.model';
import { useGetPublicLoadEquipmentQuery } from '../../redux/api/equipment/equipmentApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import DateUtils from '../../utils/dateUtils';
import {
  LiveTrackingOrderIndicator,
  LiveTrackingTripIndicator
} from '../../components/TwLiveTrackingIndicator/TwLiveTrackingIndicator';
import TwLiveTrackingAlert, {
  LiveEntityType,
  OrderTripLiveStatus
} from '../../components/TwLiveTrackingAlert/TwLiveTrackingAlert';
import { getLiveStatus, getLiveTrackEntity } from '../../utils/liveTrackingUtils';
import { PROVIDER } from '../../providers/User/User.model';
import useAuth from '../../providers/Auth/Auth.provider';
import TwEditDateTime from '../../components/TwEditDateTime/TwEditDateTime';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { closeEditDateTime } from '../../redux/features/navigation/navigationSlice';
import { setLoadId } from '../../redux/features/publicLoad/publicLoadSlice';
import { Stack, Typography } from '@mui/material';
import { TRIP_STATUS, TripDriverAckStatus, TripStop } from '../../redux/api/trips/trips.model';
import {
  CLIENT_TYPE,
  ORDER_STATUS,
  Order,
  SharedLinkTenant,
  SharedTokenResponse,
  UpdateOrderStopByTokenParams
} from '../../redux/api/orders/orders.model';
import {
  useLazyGetPublicOrderQuery,
  useUpdateOrderStopByTokenMutation
} from '../../redux/api/orders/ordersApi';
import SharedLinkTabs from './components/SharedLinkTabs';
import SharedLinkTabContent from './components/SharedLinkTabContent';
import { setUserLtlEnabled } from '../../redux/features/user/userSlice';
import { identifyAmplitudeUserFromLink } from '../../utils/amplitudeUtils';
import { TwDriverAckConfirmationModal } from '../../components/TwDriverAckConfirmationModal/TwDriverAckConfirmationModal';
import {
  DriverAckType,
  OwnerOperatorDriverAckType,
  TenantDriverSettings
} from '../../redux/api/tenantDriverSettings/tenantDriverSettings.model';
import { DRIVER_TYPE } from '../../redux/api/driver/driver.model';
import { TwRejectedTrip } from '../../components/TwRejectedTrip/TwRejectedTrip';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';

const DEFAULT_MAP_CENTER = { lat: 39.7567241, lng: -101.6269877 };
const MAX_STOP_MARKER_COUNT = 25;

const isWebInFlutter = (): boolean => {
  return navigator.userAgent.includes('Flutter');
};

const PublicLoadPage: FC = () => {
  const dispatch = useAppDispatch();
  const { loaded: googleMapsLoaded } = useGoogleMaps();
  const { token } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { updateTenantSetting } = useAuth();

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const matchesLaptopOrDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [, setMatchesMobileOrFlutterApp] = useState<boolean>(false);

  const LDClient = useLDClient();
  const mapRef = useRef<HTMLDivElement | null>(null);

  const isEditDateTimeOpen = useAppSelector((state) => state.navigation.isEditDateTimeOpen);
  const editDateTimeConfig = useAppSelector((state) => state.publicLoad.editDateTimeConfig);
  const isLtlEnabled = useAppSelector((state) => state.user.isLtlEnabled);

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [statusTag, setStatusTag] = useState<string>('');
  const [publicLoadData, setPublicLoadData] = useState<SharedTokenResponse | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [map, setMap] = useState<google.maps.Map>();
  const [markers, setMarkers] = useState<LocationMarker[]>([]);
  const [primaryClient, setPrimaryClient] = useState<LoadClient | null>(null);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [, setIsEnabledRequireBol] = useState<boolean>(false);
  const [, setCustomerCanSeeTrips] = useState<boolean>(false);
  const [, setBrowserOnline] = useState<boolean>(true);
  const [, setStops] = useState<TripStop[]>([]);

  // driver acknowledge
  const [openDriverAckConfirmation, setOpenDriverAckConfirmation] = useState<boolean>(false);
  const [enableDriverAcknowledge, setEnableDriverAcknowledge] = useState<boolean>(false);
  const [isDriverAckSettingOn, setIsDriverAckSettingOn] = useState<boolean>(false);
  const [tenantDriverSettings, setTenantDriverSettings] = useState<Pick<
    TenantDriverSettings,
    'company_driver_ack_type' | 'owner_operator_driver_ack_type'
  > | null>(null);

  const [getPublicOrder] = useLazyGetPublicOrderQuery();
  const [updateOrderStopByToken] = useUpdateOrderStopByTokenMutation();

  const { enableHideGoogleMapsAndRoutingCalculations } = useFeatureFlags();

  const { getAndRenderRoute } = useDirections({
    map: map,
    renderOnMap: true,
    renderOptions: {
      polylineOptions: {
        strokeColor: '#7E858E',
        strokeOpacity: 0.5,
        strokeWeight: 4
      },
      suppressMarkers: true
    }
  });

  // Lazy load the getAndRenderRoute function to avoid unnecessary calculations
  const lazyGetAndRenderRoute = useCallback(
    async (directionsRequest: google.maps.DirectionsRequest) => {
      if (!getAndRenderRoute || !publicLoadData) return;

      if (!showMap) return;

      // Determine if we should use Google Maps routing based on publicLoadData
      let shouldUseGoogleMapsRouting = true; // Default to true (use Google Maps)
      if (publicLoadData.trip) {
        // Driver link context - Use correct properties
        if (
          publicLoadData.trip.loaded_mileage_source === PROVIDER.PC ||
          publicLoadData.trip.empty_mileage_source === PROVIDER.PC
        ) {
          shouldUseGoogleMapsRouting = false;
        }
      } else if (publicLoadData.order?.trips?.[0]) {
        // Public link context - check the first trip - Use correct properties
        if (
          publicLoadData.order.trips[0].loaded_mileage_source === PROVIDER.PC ||
          publicLoadData.order.trips[0].empty_mileage_source === PROVIDER.PC
        ) {
          shouldUseGoogleMapsRouting = false;
        }
      }

      try {
        // Pass the calculated flag to getAndRenderRoute
        // (Assuming the second arg controls Google vs PC Miler routing)
        await getAndRenderRoute(directionsRequest, shouldUseGoogleMapsRouting);
      } catch (error) {
        console.error('DIRECTIONS RESULT ERROR', error);
        map?.setZoom(3);
      }
    },
    [getAndRenderRoute, publicLoadData, map, showMap] // Added publicLoadData dependency
  );

  useEffect(() => {
    setMatchesMobileOrFlutterApp(matchesMobileOrTablet || isWebInFlutter());
  }, []);

  useEffect(() => {
    if (LDClient && publicLoadData?.tenant) {
      identifyLDUserForPublicLoad(publicLoadData.tenant);
    }
  }, [LDClient, publicLoadData]);

  useEffect(() => {
    if (!!publicLoadData?.tenant) {
      // TODO test logrocket
      addLogRocketIdentityForSharedLink(publicLoadData);
      identifyAmplitudeUserFromLink(publicLoadData);

      setShowMap(
        publicLoadData.driver
          ? publicLoadData?.tenant?.tenantSettings?.['show-route-map-to-drivers']
          : true
      );
    }
  }, [publicLoadData]);

  const identifyLDUserForPublicLoad = (tenant: PublicLoadTenant | SharedLinkTenant): void => {
    const key = localStorage.getItem('ld:$anonUserId') as string;
    const config = {
      key,
      anonymous: true,
      custom: {
        tenantId: tenant.id,
        company: tenant.company_name
      }
    };

    LDClient?.waitForInitialization().then(() => {
      LDClient?.identify(config);

      const driverAcknowledge = LDClient?.variation('driver-acknowledge', false);
      setEnableDriverAcknowledge(driverAcknowledge);
    });
  };

  useEffect(() => {
    if (!publicLoadData?.tenant) return;

    const { tenantSettings, tenantDriverSettings } = publicLoadData?.tenant;

    setIsEnabledRequireBol(tenantSettings?.['require-bol']);
    setCustomerCanSeeTrips(tenantSettings?.['customer-access-trips']);

    // driver ack
    setIsDriverAckSettingOn(tenantSettings?.['driver-dispatch-ack'] || false);
    setTenantDriverSettings(tenantDriverSettings);
  }, [publicLoadData?.tenant]);

  useEffect(() => {
    const online = navigator.onLine;
    if (!online) {
      setBrowserOnline(false);
    } else {
      setBrowserOnline(true);
    }
  }, [navigator.onLine]);

  const getPublicLoad = useCallback(async () => {
    try {
      if (!token) return;

      const sharedLinkData = await getPublicOrder(token).unwrap();

      const { order, tenant } = sharedLinkData;
      const primaryClient = order
        ? order.primary_client === CLIENT_TYPE.BROKER
          ? order.broker
          : order.shipper
        : null;

      if (
        order &&
        order.order_type === LOAD_TYPE.SIMPLE &&
        order.trips &&
        order.trips.length > 0 &&
        order.trips[0].stops
      ) {
        order.trips[0].stops.map((s: any) => ({
          id: s.id || '',
          load_id: order.id,
          location_id: s.location_id || '',
          order: s.order,
          date: s.start_date
        }));
      }

      setPrimaryClient(primaryClient);
      setPublicLoadData(sharedLinkData);
      dispatch(setUserLtlEnabled(tenant?.ltlEnabled || false));
      dispatch(setLoadId(order?.id || ''));
      setOrder(order || null);
      updateTenantSetting(tenant.tenantSettings);
      setLoading(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      addNotification({
        message: 'Something went wrong. Please try again.',
        variant: 'error'
      });
      setLoading(false);
    }
  }, [token]);

  const editDateTime = async (ISODate: string): Promise<void> => {
    if (!token) return;

    const body: UpdateOrderStopByTokenParams = {
      trip_id: editDateTimeConfig.segment_id,
      stop_id: editDateTimeConfig.load_stop_id
    };

    if (editDateTimeConfig.type === 'in') body.stop_in_time = ISODate;
    if (editDateTimeConfig.type === 'out') body.stop_out_time = ISODate;

    try {
      setLoading(true);
      await updateOrderStopByToken({ token, data: body });

      addNotification({
        message: `Stop ${editDateTimeConfig.type} updated successfully`,
        variant: 'success'
      });

      dispatch(closeEditDateTime());
      await getPublicLoad();
    } catch {
      addNotification({
        message: `Something went wrong. Please try again.`,
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async (): Promise<void> => {
      await getPublicLoad();
    })();
  }, []);

  // Handle route rendering.
  useEffect(() => {
    if (
      loading ||
      !publicLoadData ||
      (!publicLoadData.order && !publicLoadData.trip) ||
      !googleMapsLoaded ||
      !showMap
    )
      return;

    if (publicLoadData.driver && publicLoadData.isDriverAssignedToTrip) {
      // Driver link
      const trip = publicLoadData.trip;
      setStatusTag(StringUtils.getSegmentStatusLabel(trip?.status as TRIP_STATUS));
      let totalStopsBefore = 0;
      // if split order then calculate the starting stop order
      totalStopsBefore =
        trip?.orders &&
        trip?.orders.length == 1 &&
        trip?.orders[0].order_type === LOAD_TYPE.SEGMENT &&
        trip?.orders[0].full_route_stop_overview
          ? LoadUtils.getPreviousStopsCountForTrip(trip.orders[0].full_route_stop_overview, trip.id)
          : 0;

      setMapMarkers(trip?.stops || [], totalStopsBefore);
    } else {
      // Public link
      const order = publicLoadData.order;
      setStatusTag(StringUtils.getStatusLabel(order?.status));
      setMapMarkers(LoadUtils.getAllStops(order, publicLoadData?.tenant?.ltlEnabled));
    }
  }, [publicLoadData, loading, googleMapsLoaded, showMap]);

  useEffect(() => {
    if (!publicLoadData) return;

    if (!showMap) return;
    if (publicLoadData?.driver) {
      // Driver link
      const trip = publicLoadData.trip;
      if (currentTab === 0) {
        setStatusTag(
          trip?.status !== TRIP_STATUS.DRAFT
            ? StringUtils.getSegmentStatusLabel(trip?.status as TRIP_STATUS)
            : ''
        );

        let totalStopsBefore = 0;
        // if split order then calculate the starting stop order
        totalStopsBefore =
          trip?.orders &&
          trip?.orders.length == 1 &&
          trip?.orders[0].order_type === LOAD_TYPE.SEGMENT &&
          trip?.orders[0].full_route_stop_overview
            ? LoadUtils.getPreviousStopsCountForTrip(
                trip.orders[0].full_route_stop_overview,
                trip.id
              )
            : 0;

        setMapMarkers(trip?.stops || [], totalStopsBefore);
      } else {
        const orders: Order[] | undefined = trip?.orders;
        if (!orders) return;
        const order = orders[currentTab - 1];
        setOrder(order);
        setStatusTag(StringUtils.getOrderStatusLabel(order.status));
        setMapMarkers(LoadUtils.getAllStops(order, publicLoadData?.tenant?.ltlEnabled), 1);
      }

      return;
    } else {
      // public link
      const order = publicLoadData.order;
      if (!order) return;
      if (currentTab === 0) {
        setStatusTag(StringUtils.getOrderStatusLabel(order.status));
        setMapMarkers(LoadUtils.getAllStops(order, publicLoadData?.tenant?.ltlEnabled), 1);
      } else if (order.trips && order.trips.length >= currentTab) {
        const trips = order.trips;
        if (!trips || trips.length == 0) return;
        const trip = order.trips[currentTab - 1];
        // Always show load status here
        setStatusTag(StringUtils.getOrderStatusLabel(order.status));

        // if segmented order then get total stops before current segment. This is to show correct order of stops on map
        let totalStopsBefore = 0;
        if (order.order_type === LOAD_TYPE.SEGMENT) {
          const currentTripIndex = currentTab - 1;
          totalStopsBefore = LoadUtils.getPreviousStopsCount(trips, currentTripIndex);
        }
        setMapMarkers(trip?.stops || [], totalStopsBefore);
      }
    }
  }, [currentTab, publicLoadData, showMap]);

  // Effect to re-format stops order
  useEffect(() => {
    if (!order) return;

    const stops = order.full_route_stop_overview?.map((stop, index) => {
      return { ...stop, order: index + 1, sort_order: index + 1 };
    });

    setStops(stops || []);
  }, [order]);

  /** A re-usable function to set map, either full or segment*/
  const setMapMarkers = (stops: TripStop[], startOrder?: number): Promise<void> => {
    return (async (): Promise<void> => {
      if (stops && stops.length <= MAX_STOP_MARKER_COUNT) {
        const markers: LocationMarker[] = getMapMarkers(
          stops[0]?.location || undefined,
          stops[stops.length - 1]?.location || undefined,
          stops
            .slice(1, -1)
            .filter((s) => !!s && !!s.location)
            .map((s) => s.location) as LoadLocation[],
          (startOrder ? startOrder : stops[0]?.sort_order + 1) ?? 1
        );

        if (markers.length >= 2) {
          setMarkers(markers);

          // start is first marker
          const start = markers[0];
          // end is last marker
          const end = markers[markers.length - 1];
          // stops are all markers except start and end
          const stops = markers.slice(1, markers.length - 1);

          // Render route
          const directionsRequest: google.maps.DirectionsRequest = {
            avoidFerries: true,
            origin: start.position,
            destination: end.position,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.IMPERIAL
          };

          if (stops.length > 0) {
            directionsRequest.waypoints = stops.map((s) => ({
              location: s.position,
              stopover: true
            }));
          }

          if (getAndRenderRoute) {
            try {
              // Use the lazy function instead of direct call - no second argument needed now
              await lazyGetAndRenderRoute(directionsRequest);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('DIRECTIONS RESULT ERROR', error);
              map?.setZoom(3);
            }
          }
        }
      }
    })();
  };

  // live tracking states
  const [currentTruckId, setCurrentTruckId] = useState<string | undefined | null>(undefined);

  const { data: truck, refetch: refetchTruck } = useGetPublicLoadEquipmentQuery(
    token && currentTruckId
      ? {
          token: token,
          equipmentId: currentTruckId
        }
      : skipToken
  );
  const [liveMarkers, setLiveMarkers] = useState<LiveLocationMarker[]>([]);

  const [liveStatus, setLiveStatus] = useState<OrderTripLiveStatus>(OrderTripLiveStatus.notStarted);
  const [liveTrackedEntity, setLiveTrackedEntity] = useState(LiveEntityType.order);

  useEffect(() => {
    if (!order) return;
    if (publicLoadData?.driver) return;
    setCurrentTruckId(undefined);

    if (order && order.order_type === LOAD_TYPE.SIMPLE) {
      setCurrentTruckId(order?.trips[0]?.truck_id);
    } else if (currentTab === 0) {
      // select truck from segment that is in transit for full route tab
      const tripInTransit = order?.trips?.find((trip) => trip.status === TRIP_STATUS.IN_TRANSIT);
      setCurrentTruckId(tripInTransit?.truck_id);
    } else if (order.trips && order.trips.length > 0) {
      // select truck from current segment for segment tab
      const currentTrip = order.trips[currentTab - 1];
      setCurrentTruckId(currentTrip?.truck_id);
    }
    truck && refetchTruck();
    setLiveStatus(getLiveStatus(order, currentTab, order.trips));
    setLiveTrackedEntity(getLiveTrackEntity(currentTab));
  }, [currentTab, order]);

  useEffect(() => {
    setLiveMarkers([]);
    if (!order) return;
    if (publicLoadData?.driver) return;

    const truckHasTrackingInfo =
      truck?.latest_reading?.latitude && truck?.latest_reading?.longitude;
    if (!truckHasTrackingInfo) return;

    let showLiveTracking = false;

    if (order?.order_type === LOAD_TYPE.SIMPLE) {
      // for simple load check load status
      if (order?.status === ORDER_STATUS.IN_TRANSIT) showLiveTracking = true;
    } else {
      const isFullRouteTab = currentTab === 0;
      if (isFullRouteTab) {
        // for full route tab check if any trip is in transit
        const segmentInTransit = order?.trips?.find(
          (segment) => segment.status === TRIP_STATUS.IN_TRANSIT
        );
        if (segmentInTransit) {
          showLiveTracking = true;
        }
      } else if (!isFullRouteTab) {
        // for segment tab check if trip is in transit
        if (
          order?.trips &&
          order?.trips?.length > 0 &&
          order?.trips[currentTab - 1]?.status === TRIP_STATUS.IN_TRANSIT
        ) {
          showLiveTracking = true;
        }
      }
    }

    showLiveTracking &&
      truck?.latest_reading?.latitude &&
      truck?.latest_reading?.longitude &&
      setLiveMarkers([
        getLiveLocationMarker(
          parseFloat(truck?.latest_reading?.latitude),
          parseFloat(truck?.latest_reading?.longitude),
          DateUtils.getFullLocaleDate(truck?.latest_reading?.truckercloud_reading_timestamp),
          DateUtils.getFullLocaleDate(truck?.latest_reading?.last_attempted_reading_timestamp)
        )
      ]);
  }, [currentTruckId, truck, order, currentTab]);

  const formatTitle = (): string => {
    if (!publicLoadData) return '';

    let title = '';
    if (isDriverLink) {
      if (isLtlEnabled) {
        title = `Trip #${publicLoadData.trip?.readable_id || ''}`;
      } else {
        const orders = publicLoadData.trip?.orders;
        if (orders && orders.length > 0) {
          const trip_sort_order = publicLoadData.trip?.sort_order || 0;

          title = `Trip #${orders[0]?.readable_id || ''}-${trip_sort_order + 1}`;
        }
      }
    } else {
      title = `${primaryClient?.name} • Order #${order?.readable_id}`;
    }

    return title;
  };

  const isDriverLink = !!publicLoadData?.driver ? true : false;

  const showDriverAckConfirmationModal = useMemo(() => {
    if (!enableDriverAcknowledge) return false;
    if (!isDriverLink) return false;
    if (!isDriverAckSettingOn) return false;
    if (!tenantDriverSettings) return false;
    if (!publicLoadData) return false;

    const { trip, driver } = publicLoadData;

    if (!trip || !driver) return false;

    const tripDriver = trip.drivers.find((d) => d.id === driver.id);

    if (!tripDriver) return false;

    // Company driver
    if (driver.type === DRIVER_TYPE.COMPANY_DRIVER || !tripDriver.type) {
      // if company driver ack type setting is not CONFIRMATION, then don't show the modal
      if (tenantDriverSettings.company_driver_ack_type !== DriverAckType.CONFIRMATION) return false;

      const { driver_ack_status } = tripDriver;

      // if trip driver ack status is CONFIRMED, then don't show the modal
      if (driver_ack_status === TripDriverAckStatus.CONFIRMED) return false;

      (driver_ack_status === null || driver_ack_status === TripDriverAckStatus.SENT) &&
        setOpenDriverAckConfirmation(true);
    }

    // Owner operator driver
    if (driver.type === DRIVER_TYPE.OWNER_OPERATOR_DRIVER) {
      // if owner operator driver ack type setting is not CONFIRMATION, then don't show the modal
      if (
        tenantDriverSettings.owner_operator_driver_ack_type !==
        OwnerOperatorDriverAckType.CONFIRMATION
      )
        return false;

      const { driver_ack_status } = tripDriver;

      // if trip driver ack status is CONFIRMED, then don't show the modal
      if (driver_ack_status === TripDriverAckStatus.CONFIRMED) return false;

      (driver_ack_status === null || driver_ack_status === TripDriverAckStatus.SENT) &&
        setOpenDriverAckConfirmation(true);
    }

    return true;
  }, [
    isDriverLink,
    tenantDriverSettings,
    isDriverAckSettingOn,
    tenantDriverSettings,
    publicLoadData
  ]);

  const showRejectedTrip = useMemo(() => {
    if (!enableDriverAcknowledge) return false;
    if (!isDriverAckSettingOn) return false;
    if (!tenantDriverSettings) return false;
    if (!publicLoadData) return false;
    if (!publicLoadData.trip) return false;
    if (!publicLoadData.driver) return false;

    const driver = publicLoadData.driver;
    const trip = publicLoadData.trip;

    const tripDriver = trip.drivers.find((d) => d.id === driver?.id);
    const isRejectedTrip = tripDriver?.driver_ack_status === TripDriverAckStatus.REJECTED;

    return isRejectedTrip;
  }, [publicLoadData, enableDriverAcknowledge, isDriverAckSettingOn, tenantDriverSettings]);

  return (
    <>
      {showRejectedTrip ? (
        <Box className="container">
          <Box component="header" className="rejected-trip-header">
            <TruckbaseLogo className="header__logo" />
          </Box>
          <TwRejectedTrip />
        </Box>
      ) : (
        <>
          <Box className="container">
            <Box component="header" className="header">
              {matchesMobileOrTablet ? (
                <Stack flexDirection={'row'} alignItems={'center'}>
                  <MobileLogo className="header__mobile-logo" />
                  <Typography fontSize={'18px'} fontWeight={600}>
                    {formatTitle()}
                  </Typography>
                </Stack>
              ) : (
                <TruckbaseLogo className="header__logo" />
              )}
              <TwButton
                label="Sign in"
                type={TwButtonType.secondary}
                action={(): void => navigate('/signin')}
              />
            </Box>

            {!enableHideGoogleMapsAndRoutingCalculations &&
              matchesMobileOrTablet &&
              !publicLoadData?.driver && (
                <TwLiveTrackingAlert
                  status={liveStatus}
                  equipment={truck}
                  entityType={liveTrackedEntity}
                  timestamp={
                    truck?.latest_reading?.truckercloud_reading_timestamp
                      ? DateUtils.getFullLocaleDate(
                          truck?.latest_reading?.truckercloud_reading_timestamp
                        )
                      : undefined
                  }
                />
              )}

            {publicLoadData && matchesMobileOrTablet && (
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff' }}>
                  <SharedLinkTabs
                    sharedTokenResponse={publicLoadData}
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                  />
                </Box>
              </Box>
            )}

            {googleMapsLoaded && showMap && !enableHideGoogleMapsAndRoutingCalculations && (
              <TwMap
                map={map}
                setMap={setMap}
                mapRef={mapRef}
                className="driver-link__map"
                center={DEFAULT_MAP_CENTER}
                zoom={2}
                disableDefaultUI
                keyboardShortcuts={false}
                mapTypeControl={!!publicLoadData?.driver ? false : true}
                mapTypeControlOptions={{
                  mapTypeIds: ['roadmap', 'satellite', 'terrain'],
                  position: google.maps.ControlPosition.TOP_LEFT,
                  style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR
                }}
                showReloadButton={!publicLoadData?.driver && liveMarkers && liveMarkers.length > 0}
                onReload={(): Promise<void> => {
                  return new Promise((resolve) => {
                    refetchTruck()
                      .catch(() => {
                        addNotification({
                          message: "Couldn't fetch new location. Please try again.",
                          variant: 'error'
                        });
                      })
                      .finally(() => {
                        resolve();
                      });
                  });
                }}
              >
                {markers.map((marker, index) => (
                  <TwMarker
                    key={marker.order || index}
                    position={marker.position}
                    icon={getMarkerIcon(marker.type)}
                    label={{
                      color: '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: '500',
                      text: marker?.order?.toString() || '',
                      fontFamily: 'Poppins'
                    }}
                    map={map}
                  />
                ))}

                {liveMarkers.map((marker, index) => (
                  <TwMarker
                    key={markers.length + index + 1}
                    map={map}
                    position={marker.position}
                    icon={getMarkerIcon(marker.type)}
                    timestamp={marker.timestamp}
                    type={LocationMarkerType.LIVE_LOCATION}
                    lastUpdatedTimestamp={marker.lastUpdatedTimestamp}
                  />
                ))}
              </TwMap>
            )}

            <Box className="data-container">
              <Box
                component="section"
                className="title-section"
                flexDirection={
                  matchesMobileOrTablet ||
                  (order && order.tags?.length > 5 && matchesLaptopOrDesktop)
                    ? 'column'
                    : 'row'
                }
                alignItems={
                  matchesMobileOrTablet ||
                  (order && order.tags?.length > 5 && matchesLaptopOrDesktop)
                    ? 'left'
                    : 'center'
                }
              >
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-start"
                  alignItems="center"
                  component="div"
                  className="title-section--title"
                  sx={{ width: '100%' }}
                >
                  {formatTitle()}
                  {statusTag && <span className="status">{statusTag}</span>}
                  {currentTab === 0 && !matchesMobileOrTablet && (
                    <LiveTrackingOrderIndicator orderStatus={order?.status} equipment={truck} />
                  )}
                  {currentTruckId && !matchesMobileOrTablet && (
                    <LiveTrackingTripIndicator
                      tripStatus={order?.trips[currentTab - 1]?.status}
                      equipment={truck}
                    />
                  )}
                </Stack>
              </Box>

              {publicLoadData && !matchesMobileOrTablet && (
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <SharedLinkTabs
                      sharedTokenResponse={publicLoadData}
                      currentTab={currentTab}
                      setCurrentTab={setCurrentTab}
                    />
                  </Box>
                </Box>
              )}

              {publicLoadData && (
                <SharedLinkTabContent
                  sharedTokenResponse={publicLoadData}
                  currentTab={currentTab}
                  enableLtl={isLtlEnabled}
                  refetchShareLinkData={getPublicLoad}
                />
              )}
            </Box>
          </Box>

          <TwEditDateTime
            open={isEditDateTimeOpen}
            prevStopOutTime={editDateTimeConfig.prevStopOutTime}
            prevDateTime={editDateTimeConfig.prevDateTime}
            datetime={editDateTimeConfig.dateTime}
            nextDateTime={editDateTimeConfig.nextDateTime}
            nextStopInTime={editDateTimeConfig.nextStopInTime}
            title={editDateTimeConfig?.title}
            dateInputLabel={editDateTimeConfig?.dateInputLabel}
            onClose={(): void => {
              dispatch(closeEditDateTime());
            }}
            onSuccess={editDateTime}
          />

          {showDriverAckConfirmationModal && openDriverAckConfirmation && (
            <TwDriverAckConfirmationModal
              open={openDriverAckConfirmation}
              onClose={(): void => setOpenDriverAckConfirmation(false)}
              driverId={publicLoadData?.driver?.id as string}
              tripId={publicLoadData?.trip?.id}
              token={token}
            />
          )}

          <Backdrop
            sx={{
              color: '#fff',
              zIndex: 10
            }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </>
      )}
    </>
  );
};

export default PublicLoadPage;

export interface TabPanelProps {
  value: number;
  children: JSX.Element | JSX.Element[];
  index: number;
  id?: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, id }): JSX.Element => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={id}
      aria-labelledby={`tw-segment-tab-${index}`}
    >
      {value === index && children}
    </div>
  );
};
